/**
 * SelectField Component (Refactored)
 * Fixed all critical issues identified in tech debt document:
 * - Removed nested button issue
 * - Proper Controller integration (no hidden input serialization)
 * - Added missing ARIA attributes
 * - Fixed performance issues with memoized index maps
 * - Proper state synchronization
 * - Uses shared FieldWrapper and ClearButton
 */

import React, { forwardRef, useState, useRef, useEffect, useMemo } from 'react';
import { Controller, Control, FieldError } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { SelectFieldProps, SelectOption } from '@defs/components';
import { FieldWrapper } from './FieldWrapper';
import { ClearButton } from './ClearButton';
import {
  buildAriaDescribedBy,
  useFormIds,
  useControllableState,
  useMemoizedIndexMap,
  useFlattenedOptions,
} from './utils';

// ============================================================================
// Styled Components
// ============================================================================

const SelectContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Select = styled.select<{
  hasError: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  padding: 0.625rem 0.75rem;
  padding-right: 2.5rem;
  min-height: 2.5rem; /* 48px touch target */
  border: 1px solid
    ${(props) => (props.hasError ? 'var(--danger-600)' : 'var(--border)')};
  border-radius: var(--radius-md);
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-primary);
  background-color: var(--surface);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  appearance: none;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--brand-600);
    box-shadow: 0 0 0 3px var(--brand-50);
    background-color: var(--surface);
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: var(--brand-500);
  }

  ${(props) =>
    props.disabled &&
    css`
      background-color: var(--neutral-100);
      color: var(--text-muted);
      cursor: not-allowed;
      opacity: 0.7;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    `}

  ${(props) =>
    props.hasError &&
    css`
      border-color: var(--danger-600);

      &:focus {
        border-color: var(--danger-600);
        box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.1);
      }
    `}

  &[aria-invalid="true"] {
    border-color: var(--danger-600);
  }

  /* Mobile: Ensure min-height and adjust padding if needed */
  @media (max-width: 767px) {
    min-height: 2.5rem; /* 48px */
    padding: 0.625rem 0.75rem;
    font-size: 1rem;
    min-height: 2.75rem;
    padding-right: 2.5rem;
  }

  @media (max-width: 479px) {
    min-height: 2.75rem; /* 52px for small mobile */
  }

  /* Style for placeholder option */
  option:first-child {
    color: var(--text-muted);
  }

  /* Group styling */
  optgroup {
    font-weight: 600;
    color: var(--text-primary);
    padding: 0.5rem 0;
  }

  optgroup option {
    font-weight: normal;
    padding-left: 1rem;
  }
`;

const CustomSelectContainer = styled.div<{
  isOpen: boolean;
  disabled?: boolean;
}>`
  position: relative;
  width: 100%;

  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.7;
    `}
`;

const CustomSelectTrigger = styled.button<{
  hasError: boolean;
  isOpen: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  padding: 0.625rem 0.75rem;
  padding-right: 3.5rem; /* Extra space for clear button */
  min-height: 2.5rem; /* 48px touch target */
  border: 1px solid
    ${(props) => (props.hasError ? 'var(--danger-600)' : 'var(--border)')};
  border-radius: var(--radius-md);
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-primary);
  background-color: var(--surface);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  &:focus {
    outline: none;
    border-color: var(--brand-600);
    box-shadow: 0 0 0 3px var(--brand-50);
    background-color: var(--surface);
  }

  &:hover:not(:disabled) {
    border-color: var(--brand-500);
  }

  ${(props) =>
    props.disabled &&
    css`
      background-color: var(--neutral-100);
      color: var(--text-muted);
      cursor: not-allowed;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border-color: var(--danger-600);

      &:focus {
        border-color: var(--danger-600);
        box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.1);
      }
    `}

  ${(props) =>
    props.isOpen &&
    css`
      border-color: var(--brand-600);
      box-shadow: 0 0 0 3px var(--brand-50);
      background-color: var(--surface);
    `}

  /* Mobile: Adjust padding and ensure min-height */
  @media (max-width: 767px) {
    min-height: 2.5rem; /* 48px */
    padding: 0.625rem 0.75rem;
    font-size: 1rem;
    min-height: 2.75rem;
    padding-right: 3.5rem;
  }

  @media (max-width: 479px) {
    min-height: 2.75rem; /* 52px for small mobile */
  }
`;

const TriggerContent = styled.div`
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-right: 0.5rem;
`;

const TriggerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
`;

const SelectIcon = styled.div<{ isOpen: boolean }>`
  color: var(--text-muted);
  font-size: 1.25rem;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  pointer-events: none;
`;

const CustomDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin-top: 0.25rem;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--neutral-100);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--text-muted);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-primary);
  }
`;

const OptionItem = styled.div<{
  isSelected: boolean;
  isHighlighted: boolean;
  disabled?: boolean;
}>`
  padding: 0.75rem 1rem;
  min-height: 2.75rem; /* 44px touch target */
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: var(--text-primary);
  transition: background-color 0.1s ease;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;

  ${(props) =>
    props.disabled &&
    css`
      color: var(--text-muted);
      cursor: not-allowed;
      opacity: 0.6;
    `}

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        background-color: var(--brand-50);
      }
    `}

  ${(props) =>
    props.isHighlighted &&
    !props.disabled &&
    css`
      background-color: var(--brand-100);
    `}

  ${(props) =>
    props.isSelected &&
    css`
      background-color: var(--action-primary);
      color: white;
      font-weight: 500;

      &:hover {
        background-color: var(--action-primary-hover);
      }
    `}

  /* Mobile: Increase touch target */
  @media (max-width: 767px) {
    padding: 0.875rem 1.25rem;
    min-height: 2.75rem; /* 44px */
  }
`;

const GroupHeader = styled.div`
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-muted);
  background-color: var(--neutral-25);
  border-bottom: 1px solid var(--border);
  text-transform: none;
  letter-spacing: 0.05em;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 1px solid var(--border);
  font-size: 0.95rem;
  background: var(--surface);
  color: var(--text-primary);

  &:focus {
    outline: none;
    background-color: var(--brand-50);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const NoOptions = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.9rem;
`;

const LoadingIndicator = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &::before {
    content: '⏳';
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// ============================================================================
// Component Interface Extensions
// ============================================================================

interface ExtendedSelectFieldProps extends Omit<SelectFieldProps, 'onChange'> {
  // React Hook Form integration
  control?: Control<any>;
  error?: FieldError | string | undefined;

  // Enhanced event handlers
  onChange?: (
    value: string | string[] | null,
    option?: SelectOption | SelectOption[],
  ) => void;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;

  // Enhanced functionality
  searchPlaceholder?: string;
  emptyMessage?: string;
  groupBy?: keyof SelectOption;

  // Additional HTML props
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: boolean;
  role?: string;
}

// ============================================================================
// SelectField Component
// ============================================================================

export const SelectField = forwardRef<
  HTMLSelectElement,
  ExtendedSelectFieldProps
>(
  (
    {
      label,
      name,
      options = [],
      placeholder,
      required = false,
      disabled = false,
      readOnly = false,
      value,
      defaultValue,
      error,
      helperText,
      multiple = false,
      searchable = false,
      clearable = false,
      loading = false,
      noOptionsMessage = 'No hay opciones disponibles',
      loadingMessage = 'Cargando...',
      searchPlaceholder = 'Buscar...',
      emptyMessage = 'No se encontraron resultados',
      control,
      onChange,
      onBlur,
      onFocus,
      className,
      id,
      testId,
      'data-testid': dataTestId,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid,
      groupBy,
    },
    ref,
  ) => {
    // Generate unique IDs
    const ids = useFormIds(name, id);

    // State management
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Controlled/uncontrolled state management - ALL HOOKS MUST BE AT TOP LEVEL
    const normalizedValue: string | string[] | null =
      value !== undefined
        ? multiple
          ? Array.isArray(value)
            ? value.map((v) => v.toString())
            : [value.toString()]
          : value.toString()
        : null;

    const normalizedDefaultValue: string | string[] | null = multiple
      ? Array.isArray(defaultValue)
        ? defaultValue.map((v) => v.toString())
        : []
      : defaultValue?.toString() || '';

    const [internalValue, setInternalValue] = useControllableState<
      string | string[] | null
    >({
      value: normalizedValue,
      defaultValue: normalizedDefaultValue,
      ...(onChange && { onChange: (value) => onChange(value) }),
    });

    // Determine error message
    const errorMessage = typeof error === 'string' ? error : error?.message;
    const hasError = !!errorMessage;

    // Build aria-describedby
    const describedByIds = buildAriaDescribedBy([
      ariaDescribedBy,
      hasError ? ids.errorId : null,
      helperText && !hasError ? ids.helperId : null,
    ]);

    // Filter and group options (memoized for performance)
    const filteredOptions = useMemo(() => {
      if (!searchable || !searchTerm) return options;

      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.value
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }, [options, searchable, searchTerm]);

    const groupedOptions = useMemo(() => {
      if (!groupBy) return { '': filteredOptions };

      return filteredOptions.reduce(
        (groups, option) => {
          const group =
            ((option as unknown as Record<string, unknown>)[
              groupBy
            ] as string) || 'Other';
          if (!groups[group]) groups[group] = [];
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          groups[group]!.push(option);
          return groups;
        },
        {} as Record<string, SelectOption[]>,
      );
    }, [filteredOptions, groupBy]);

    // Create flattened options for keyboard navigation (memoized)
    const flatOptions = useFlattenedOptions(groupedOptions);

    // Create index map for O(1) lookups (replaces O(n²) indexOf)
    const optionIndexMap = useMemoizedIndexMap(
      flatOptions,
      (option) => option.value,
    );

    // Helper functions
    const isOptionSelected = (
      option: SelectOption,
      currentValue: string | string[],
    ) => {
      if (multiple) {
        return (
          Array.isArray(currentValue) &&
          currentValue.includes(option.value.toString())
        );
      }
      return currentValue === option.value.toString();
    };

    const getSelectedOptions = (currentValue: string | string[]) => {
      if (multiple) {
        return Array.isArray(currentValue)
          ? options.filter((opt) => currentValue.includes(opt.value.toString()))
          : [];
      }
      return options.find((opt) => opt.value.toString() === currentValue);
    };

    const getDisplayValue = (currentValue: string | string[]) => {
      if (multiple) {
        const selectedOptions = getSelectedOptions(
          currentValue,
        ) as SelectOption[];
        return selectedOptions.length > 0
          ? selectedOptions.map((opt) => opt.label).join(', ')
          : placeholder || 'Seleccionar...';
      }
      const selectedOption = getSelectedOptions(currentValue) as
        | SelectOption
        | undefined;
      return selectedOption
        ? selectedOption.label
        : placeholder || 'Seleccionar...';
    };

    // Handle option selection
    const handleOptionSelect = (
      option: SelectOption,
      currentValue: string | string[],
    ) => {
      if (option.disabled) return null;

      let newValue: string | string[];
      let selectedOption: SelectOption | SelectOption[];

      if (multiple) {
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        const optionValue = option.value.toString();

        if (currentArray.includes(optionValue)) {
          newValue = currentArray.filter((v) => v !== optionValue);
        } else {
          newValue = [...currentArray, optionValue];
        }

        selectedOption = options.filter((opt) =>
          newValue.includes(opt.value.toString()),
        );
      } else {
        newValue = option.value.toString();
        selectedOption = option;
        // Close dropdown after selection for single-select
        // Use setTimeout to ensure selection is processed first
        setTimeout(() => {
          setIsOpen(false);
        }, 0);
      }

      return {
        newValue: newValue.length === 0 ? null : newValue,
        selectedOption,
      };
    };

    // Handle clear
    const handleClear = () => {
      const newValue = multiple ? null : null;
      const selectedOption = multiple ? [] : undefined;
      return { newValue, selectedOption };
    };

    // Keyboard navigation
    const handleKeyDown = (
      e: React.KeyboardEvent,
      currentValue: string | string[],
    ) => {
      if (disabled || readOnly) return;

      switch (e.key) {
        case 'Enter':
        case ' ':
          if (!isOpen) {
            setIsOpen(true);
            e.preventDefault();
          } else if (highlightedIndex >= 0) {
            const selectedOption = flatOptions[highlightedIndex];
            if (selectedOption) {
              const result = handleOptionSelect(selectedOption, currentValue);
              if (result) {
                onChange?.(result.newValue, result.selectedOption);
              }
            }
            e.preventDefault();
          }
          break;
        case 'Escape':
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) =>
              prev < flatOptions.length - 1 ? prev + 1 : 0,
            );
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : flatOptions.length - 1,
            );
          }
          break;
      }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;

        // Check if click is outside the container
        if (containerRef.current && !containerRef.current.contains(target)) {
          // Add small delay to allow option clicks to complete
          setTimeout(() => {
            setIsOpen(false);
          }, 0);
        }
      };

      if (isOpen) {
        // Use capture phase to ensure we get the event before option clicks
        document.addEventListener('mousedown', handleClickOutside, true);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    const preventScrollOnFocus = (
      element: HTMLElement | null,
      enable: boolean,
    ) => {
      if (!element) {
        return undefined;
      }

      return (event: FocusEvent) => {
        if (!enable) {
          return;
        }

        const target = event.target as HTMLElement | null;

        if (!target || !element.contains(target)) {
          return;
        }

        const previousScrollPositions: Array<[HTMLElement, number, number]> =
          [];

        let current: HTMLElement | null = element;

        while (current) {
          previousScrollPositions.push([
            current,
            current.scrollTop,
            current.scrollLeft,
          ]);
          current = current.parentElement;
        }

        requestAnimationFrame(() => {
          previousScrollPositions.forEach(([node, top, left]) => {
            if (node.scrollTop !== top) {
              node.scrollTop = top;
            }

            if (node.scrollLeft !== left) {
              node.scrollLeft = left;
            }
          });
        });
      };
    };

    const focusHandlerRef = useRef<((event: FocusEvent) => void) | undefined>(
      undefined,
    );

    useEffect(() => {
      const container = containerRef.current;

      if (!container) {
        return undefined;
      }

      if (!focusHandlerRef.current) {
        focusHandlerRef.current = preventScrollOnFocus(container, true);
      }

      const handler = focusHandlerRef.current;

      if (!handler) {
        return undefined;
      }

      container.addEventListener('focusin', handler);

      return () => {
        container.removeEventListener('focusin', handler);
      };
    }, []);

    // Render simple select for non-enhanced functionality
    if (!searchable && !multiple && !clearable && !groupBy) {
      console.log('🎯 [SelectField] Using simple select path for:', name);
      const SimpleSelectField = ({ field }: { field?: any } = {}) => (
        <FieldWrapper
          id={ids.inputId}
          label={label}
          required={required}
          disabled={disabled}
          error={errorMessage}
          helperText={helperText}
          className={className}
        >
          <SelectContainer>
            <Select
              id={ids.inputId}
              name={name}
              value={field?.value ?? (internalValue || '')}
              disabled={disabled || loading}
              hasError={hasError}
              aria-invalid={ariaInvalid ?? hasError}
              aria-describedby={describedByIds}
              aria-labelledby={ariaLabelledBy}
              aria-required={required}
              data-testid={dataTestId || testId || `select-field-${name}`}
              ref={ref}
              onChange={(e) => {
                const value = e.target.value;
                console.log(`🔽 [SelectField-Simple] ${name} onChange:`, value);
                if (field?.onChange) {
                  field.onChange(e);
                }
                if (onChange) {
                  onChange(value);
                }
              }}
              onBlur={field?.onBlur ?? onBlur}
              onFocus={onFocus}
            >
              {placeholder && (
                <option value="" disabled>
                  {placeholder}
                </option>
              )}
              {Object.entries(groupedOptions).map(
                ([groupName, groupOptions]) =>
                  groupName ? (
                    <optgroup key={groupName} label={groupName}>
                      {groupOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                        >
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ) : (
                    groupOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </option>
                    ))
                  ),
              )}
            </Select>
          </SelectContainer>
        </FieldWrapper>
      );

      // Use Controller for proper RHF integration (no hidden input)
      if (control) {
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue?.toString() || ''}
            rules={{ required: required ? `${label} es requerido` : false }}
            render={({ field }) => <SimpleSelectField field={field} />}
          />
        );
      }

      return <SimpleSelectField />;
    }

    // Enhanced custom select component
    console.log('🎯 [SelectField] Using enhanced select path for:', name);
    const EnhancedSelectField = ({
      currentValue,
      onValueChange,
    }: {
      currentValue: string | string[];
      onValueChange: (
        value: string | string[] | null,
        option?: SelectOption | SelectOption[],
      ) => void;
    }) => (
      <FieldWrapper
        id={ids.inputId}
        label={label}
        required={required}
        disabled={disabled}
        error={errorMessage}
        helperText={helperText}
        className={className}
      >
        <CustomSelectContainer
          ref={containerRef}
          isOpen={isOpen}
          disabled={disabled || loading}
        >
          <CustomSelectTrigger
            ref={triggerRef}
            type="button"
            id={ids.inputId}
            hasError={hasError}
            isOpen={isOpen}
            disabled={disabled || loading}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={ids.listboxId}
            aria-invalid={ariaInvalid ?? hasError}
            aria-describedby={describedByIds}
            aria-labelledby={ariaLabelledBy}
            aria-required={required}
            {...(multiple && { 'aria-multiselectable': true })}
            data-testid={dataTestId || testId || `select-field-${name}`}
            onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
            onKeyDown={(e) => handleKeyDown(e, currentValue)}
            onBlur={onBlur}
            onFocus={onFocus}
          >
            <TriggerContent
              style={{
                color: (
                  multiple
                    ? Array.isArray(currentValue) && currentValue.length === 0
                    : !currentValue
                )
                  ? 'var(--text-muted)'
                  : 'var(--text-primary)',
              }}
            >
              {getDisplayValue(currentValue)}
            </TriggerContent>

            <TriggerControls>
              {clearable && currentValue && (
                <ClearButton
                  onClear={() => {
                    const result = handleClear();
                    onValueChange(result.newValue, result.selectedOption);
                  }}
                  disabled={disabled}
                  position="relative"
                />
              )}

              <SelectIcon isOpen={isOpen}>▼</SelectIcon>
            </TriggerControls>
          </CustomSelectTrigger>

          <CustomDropdown
            isOpen={isOpen}
            role="listbox"
            id={ids.listboxId}
            {...(multiple && { 'aria-multiselectable': true })}
          >
            {loading ? (
              <LoadingIndicator>{loadingMessage}</LoadingIndicator>
            ) : (
              <>
                {searchable && (
                  <SearchInput
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsOpen(false);
                        triggerRef.current?.focus();
                      }
                    }}
                  />
                )}

                {filteredOptions.length === 0 ? (
                  <NoOptions>
                    {searchTerm ? emptyMessage : noOptionsMessage}
                  </NoOptions>
                ) : (
                  Object.entries(groupedOptions).map(
                    ([groupName, groupOptions]) => (
                      <React.Fragment key={groupName || 'default'}>
                        {groupName && <GroupHeader>{groupName}</GroupHeader>}
                        {groupOptions.map((option) => {
                          const flatIndex =
                            optionIndexMap.get(option.value) ?? -1;
                          const isSelected = isOptionSelected(
                            option,
                            currentValue,
                          );

                          return (
                            <OptionItem
                              key={option.value}
                              isSelected={isSelected}
                              isHighlighted={highlightedIndex === flatIndex}
                              disabled={option.disabled ?? false}
                              role="option"
                              id={`${ids.inputId}-option-${option.value}`}
                              aria-selected={isSelected}
                              onClick={(e) => {
                                // Prevent event bubbling to outside click handler
                                e.stopPropagation();
                                e.preventDefault();

                                const result = handleOptionSelect(
                                  option,
                                  currentValue,
                                );
                                if (result) {
                                  onValueChange(
                                    result.newValue,
                                    result.selectedOption,
                                  );
                                }
                              }}
                              onMouseEnter={() =>
                                setHighlightedIndex(flatIndex)
                              }
                            >
                              {option.icon && <span>{option.icon}</span>}
                              <span>{option.label}</span>
                              {isSelected && (
                                <span style={{ marginLeft: 'auto' }}>✓</span>
                              )}
                            </OptionItem>
                          );
                        })}
                      </React.Fragment>
                    ),
                  )
                )}
              </>
            )}
          </CustomDropdown>
        </CustomSelectContainer>
      </FieldWrapper>
    );

    // Use Controller for proper RHF integration (eliminates hidden input)
    if (control) {
      return (
        <Controller
          name={name}
          control={control}
          defaultValue={
            multiple
              ? Array.isArray(defaultValue)
                ? defaultValue
                : []
              : defaultValue?.toString() || ''
          }
          rules={{ required: required ? `${label} es requerido` : false }}
          render={({ field }) => (
            <EnhancedSelectField
              currentValue={
                field.value ?? (multiple ? ([] as string[]) : ('' as string))
              }
              onValueChange={(newValue, _selectedOption) => {
                field.onChange(newValue);
                // Only pass the clean value to avoid circular reference issues
                onChange?.(newValue);
              }}
            />
          )}
        />
      );
    }

    return (
      <EnhancedSelectField
        currentValue={internalValue ?? (multiple ? [] : '')}
        onValueChange={(newValue, _selectedOption) => {
          setInternalValue(newValue);
          // Only pass the clean value to avoid circular reference issues
          onChange?.(newValue);
        }}
      />
    );
  },
);

SelectField.displayName = 'SelectField';

export default SelectField;
